import React, { useMemo, useEffect, useRef, useCallback } from "react";
import { QuestionnaireProvider } from "context/questionnaire";
import { useQuestionnaire } from "context/questionnaire";
import QuestionnaireLayout from "layouts/questionnaire-layout";
import QuestionnairePage from "pages/questionnaire";
import { QuestionnaireLanguagePicker } from "components/questionnaire-language-picker";
import { Pendo } from "components/pendo";

declare global {
  interface Window {
    pendo?: {
      track?: (name: string, properties: Record<string, unknown>) => void;
    };
  }
}

interface PrevQuestion {
  index: number;
  startIndex: number;
  name: string;
  type: string;
}

const track = (event: string, props: Record<string, unknown>) =>
  window.pendo?.track?.(event, props);

const QuestionnairePendo: React.FC = () => {
  const {
    questionnaire,
    visiblePages,
    isLoading,
    currentPageIndex,
    locale,
    isComplete,
  } = useQuestionnaire();

  const prevPageRef = useRef<number>(-1);
  const enteredAtRef = useRef<number | null>(null);
  const prevQuestionRef = useRef<PrevQuestion | null>(null);

  const account = useMemo(() => {
    const orgId = questionnaire?.metadata?.organization_id;
    if (!orgId) return {};
    return { id: orgId };
  }, [questionnaire?.metadata?.organization_id]);

  const baseProps = useCallback(
    () =>
      questionnaire
        ? {
            survey_id: questionnaire.id,
            survey_name: questionnaire.name,
            locale,
          }
        : null,
    [questionnaire, locale]
  );

  const fireCompletion = useCallback(
    (exitType: string) => {
      const base = baseProps();
      const prev = prevQuestionRef.current;
      if (!base || !prev || enteredAtRef.current == null) return;

      track("questionnaire_question_completed", {
        ...base,
        question_name: prev.name,
        question_type: prev.type,
        question_index: prev.index,
        page_start_index: prev.startIndex,
        dwell_time_ms: Date.now() - enteredAtRef.current,
        exit_type: exitType,
      });
    },
    [baseProps]
  );

  useEffect(() => {
    if (!questionnaire || currentPageIndex < 0) return;
    if (currentPageIndex === prevPageRef.current) return;

    const wasLanding = prevPageRef.current === -1;
    const prev = prevQuestionRef.current;
    const base = baseProps();
    if (!base) return;

    if (prev && enteredAtRef.current != null && !wasLanding) {
      const exitType = currentPageIndex > prev.index ? "next" : "back";
      fireCompletion(exitType);
    }

    if (wasLanding) {
      track("questionnaire_started", { ...base });
    }

    prevPageRef.current = currentPageIndex;

    const page = visiblePages[currentPageIndex];
    const lead = page?.questions[0]?.data;
    if (!page || !lead) return;

    track("questionnaire_question_viewed", {
      ...base,
      question_name: lead.name,
      question_type: lead.type,
      question_index: currentPageIndex,
      page_start_index: page.startIndex,
    });

    prevQuestionRef.current = {
      index: currentPageIndex,
      startIndex: page.startIndex,
      name: lead.name,
      type: lead.type,
    };
    enteredAtRef.current = Date.now();
  }, [questionnaire, visiblePages, currentPageIndex, locale, baseProps, fireCompletion]);

  useEffect(() => {
    if (!isComplete || !questionnaire) return;
    const base = baseProps();
    if (!base) return;

    fireCompletion("submit");

    track("questionnaire_submitted", { ...base });

    prevQuestionRef.current = null;
    enteredAtRef.current = null;
  }, [isComplete, questionnaire, baseProps, fireCompletion]);

  useEffect(() => {
    const onBeforeUnload = () => {
      const base = baseProps();
      const prev = prevQuestionRef.current;
      if (!base || !prev || enteredAtRef.current == null) return;

      track("questionnaire_abandoned", {
        ...base,
        question_name: prev.name,
        question_type: prev.type,
        question_index: prev.index,
        page_start_index: prev.startIndex,
        dwell_time_ms: Date.now() - enteredAtRef.current,
      });
    };

    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [baseProps]);

  return <Pendo account={account} isDataLoading={isLoading} />;
};

const QuestionnaireContent: React.FC = () => {
  const { questionnaire, progress, currentPageIndex, restartQuestionnaire } =
    useQuestionnaire();
  const isLandingPage = currentPageIndex === -1;

  return (
    <QuestionnaireLayout
      progress={progress}
      logoUrl={questionnaire?.theme?.logo_url}
      logoMaxHeight={questionnaire?.theme?.logo_max_height}
      showProgress={!isLandingPage}
      showRestart={!isLandingPage}
      onRestart={restartQuestionnaire}
      languagePicker={<QuestionnaireLanguagePicker />}
    >
      <QuestionnairePage />
    </QuestionnaireLayout>
  );
};

const QuestionnaireRoutes: React.FC = () => {
  return (
    <QuestionnaireProvider>
      <QuestionnairePendo />
      <QuestionnaireContent />
    </QuestionnaireProvider>
  );
};

export default QuestionnaireRoutes;
